import type { CSSProperties, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { buttonLabelTitle } from '@/styles/buttonLabel'
import './AuditCtaButton.css'

type AuditCtaVariant = 'default' | 'about' | 'header' | 'compact' | 'light'

type AuditCtaButtonProps = {
  children: ReactNode
  href?: string
  to?: string
  variant?: AuditCtaVariant
  /** Smaller footprint — pairs with `light` on hero/service pages */
  compact?: boolean
  className?: string
  style?: CSSProperties
  title?: string
  as?: 'link' | 'button'
  type?: 'button' | 'submit'
  disabled?: boolean
  loading?: boolean
  success?: boolean
}

export default function AuditCtaButton({
  children,
  href = '#contact',
  to,
  variant = 'default',
  compact = false,
  className = '',
  style,
  title,
  as = 'link',
  type = 'submit',
  disabled = false,
  loading = false,
  success = false,
}: AuditCtaButtonProps) {
  const isDisabled = disabled || loading || success
  const classNames = [
    'audit-cta-button',
    `audit-cta-button--${variant}`,
    compact ? 'audit-cta-button--compact' : '',
    loading ? 'audit-cta-button--loading' : '',
    success ? 'audit-cta-button--success' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')
  const labelTitle = buttonLabelTitle(children, title)

  const content = (
    <>
      <span
        className="audit-cta-button__label"
      >
        {children}
      </span>
      <svg
        className="audit-cta-button__chevron"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="m9 18 6-6-6-6" />
      </svg>
    </>
  )

  if (as === 'button') {
    return (
      <button
        type={type}
        disabled={isDisabled}
        className={classNames}
        style={style}
        title={labelTitle}
      >
        {content}
      </button>
    )
  }

  if (to) {
    return (
      <Link to={to} className={classNames} style={style} title={labelTitle}>
        {content}
      </Link>
    )
  }

  return (
    <a href={href} className={classNames} style={style} title={labelTitle}>
      {content}
    </a>
  )
}
